import { useState } from 'react';
import {
  useAttackScenarios,
  useAttackSimRuns,
  startAttackSim,
  type AttackScenario,
} from '../../hooks/useSecOps';

const RISK_COLORS: Record<string, string> = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-zinc-700 text-zinc-400',
  running: 'bg-blue-500/20 text-blue-400',
  completed: 'bg-green-500/20 text-green-400',
  failed: 'bg-red-500/20 text-red-400',
};

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-zinc-700 text-zinc-400'}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

function StatCard({ label, value, accent }: { label: string; value: number | string; accent: string }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
      <p className="text-3xl font-bold text-gray-100">{value}</p>
      <p className="text-sm text-gray-400 mt-1">{label}</p>
      <div className={`mt-3 h-1 w-12 rounded ${accent}`} />
    </div>
  );
}

export default function AttackSimulationPage() {
  const { data: scenariosResp, loading: scenariosLoading } = useAttackScenarios();
  const { data: runsResp, loading: runsLoading, refresh: refreshRuns } = useAttackSimRuns();

  const scenarios = scenariosResp?.data ?? [];
  const runs = runsResp?.data?.runs ?? [];
  const coverage = runsResp?.data?.coverage;

  const [runModal, setRunModal] = useState<AttackScenario | null>(null);
  const [targetDevice, setTargetDevice] = useState('');
  const [starting, setStarting] = useState(false);

  const handleStartSim = async () => {
    if (!runModal || !targetDevice.trim()) return;
    setStarting(true);
    try {
      await startAttackSim({ scenario_id: runModal.id, target_device_id: targetDevice.trim() });
      setRunModal(null);
      setTargetDevice('');
      refreshRuns();
    } finally {
      setStarting(false);
    }
  };

  if (scenariosLoading && scenarios.length === 0) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto bg-[#09090b] min-h-screen">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-zinc-800 rounded w-72" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-48 bg-zinc-800 rounded-lg" />)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto bg-[#09090b] min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-100">Attack Simulation</h1>
        <p className="text-gray-400 text-sm mt-1">Run MITRE ATT&CK scenarios to validate detection coverage</p>
      </div>

      {/* Coverage Summary */}
      {coverage && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Scenarios" value={coverage.total_scenarios} accent="bg-indigo-500" />
          <StatCard label="Tested" value={coverage.tested} accent="bg-blue-500" />
          <StatCard label="Detected" value={coverage.detected} accent="bg-green-500" />
          <StatCard label="Coverage" value={`${coverage.coverage_pct}%`} accent={coverage.coverage_pct >= 80 ? 'bg-green-500' : coverage.coverage_pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'} />
        </div>
      )}

      {/* Scenario Cards */}
      <h2 className="text-lg font-semibold text-gray-100 mb-4">Attack Scenarios</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {scenarios.map((scenario) => {
          const lastRun = runs.find(r => r.scenario_id === scenario.id && r.status === 'completed');
          return (
            <div key={scenario.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <span className={`rounded px-2 py-0.5 text-xs font-mono font-medium ${RISK_COLORS[scenario.risk_level] ?? 'bg-zinc-700 text-zinc-400'}`}>
                  {scenario.mitre}
                </span>
                <Badge value={scenario.risk_level} colors={RISK_COLORS} />
              </div>
              <h3 className="text-sm font-semibold text-gray-100 mb-1">{scenario.name}</h3>
              <p className="text-xs text-gray-500 mb-1">{scenario.category}</p>
              <p className="text-xs text-gray-400 flex-1 mb-4 line-clamp-3">{scenario.description}</p>
              <div className="flex items-center justify-between">
                {lastRun ? (
                  <span className={`text-xs ${lastRun.detected ? 'text-green-400' : 'text-red-400'}`}>
                    {lastRun.detected ? 'Detected' : 'Not Detected'}
                  </span>
                ) : (
                  <span className="text-xs text-gray-600">Not tested</span>
                )}
                <button
                  onClick={() => setRunModal(scenario)}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                >
                  Run
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Run History */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg">
        <div className="px-6 py-4 border-b border-zinc-800">
          <h2 className="text-lg font-semibold text-gray-100">Run History</h2>
        </div>
        {runsLoading && runs.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading runs...</div>
        ) : runs.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No simulation runs yet. Select a scenario to begin testing your detection coverage.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-zinc-800">
                  <th className="px-6 py-3 font-medium">Scenario</th>
                  <th className="px-6 py-3 font-medium">MITRE</th>
                  <th className="px-6 py-3 font-medium">Target Device</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Detected</th>
                  <th className="px-6 py-3 font-medium">Detection Time</th>
                  <th className="px-6 py-3 font-medium">Started</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="px-6 py-3 text-gray-200">{run.scenario_name}</td>
                    <td className="px-6 py-3">
                      <span className="font-mono text-xs text-indigo-400">{run.mitre_technique}</span>
                    </td>
                    <td className="px-6 py-3 text-gray-400 font-mono text-xs">{run.target_device_id.slice(0, 12)}...</td>
                    <td className="px-6 py-3"><Badge value={run.status} colors={STATUS_COLORS} /></td>
                    <td className="px-6 py-3">
                      {run.status === 'completed' ? (
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${run.detected ? 'text-green-400' : 'text-red-400'}`}>
                          <span className={`h-2 w-2 rounded-full ${run.detected ? 'bg-green-400' : 'bg-red-400'}`} />
                          {run.detected ? 'Yes' : 'No'}
                        </span>
                      ) : (
                        <span className="text-gray-600 text-xs">--</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-gray-400 text-xs">
                      {run.detection_time_ms != null
                        ? run.detection_time_ms < 60000
                          ? `${(run.detection_time_ms / 1000).toFixed(1)}s`
                          : `${Math.round(run.detection_time_ms / 60000)}m`
                        : '--'}
                    </td>
                    <td className="px-6 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(run.started_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Run Modal */}
      {runModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setRunModal(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-100 mb-2">Run Simulation</h3>
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-xs text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded">{runModal.mitre}</span>
                <Badge value={runModal.risk_level} colors={RISK_COLORS} />
              </div>
              <p className="text-sm text-gray-200 font-medium">{runModal.name}</p>
              <p className="text-xs text-gray-400 mt-1">{runModal.description}</p>
            </div>
            <div className="mb-4">
              <label className="text-sm text-gray-300 block mb-1">Target Device ID</label>
              <input
                type="text"
                value={targetDevice}
                onChange={(e) => setTargetDevice(e.target.value)}
                placeholder="Enter device ID from RMM"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:border-indigo-500 focus:outline-none"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setRunModal(null)}
                className="text-gray-400 hover:text-gray-300 text-sm px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleStartSim}
                disabled={starting || !targetDevice.trim()}
                className="bg-red-600 hover:bg-red-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {starting ? 'Starting...' : 'Start Simulation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
